import * as excel from 'exceljs';
import { Excel } from '../model/table.model';

export interface MultiSheetPayload {
  filename: string;
  sheets: Array<{ name: string; headers: string[]; rows: Array<Array<string | number | null>> }>;
  headerColor?: string;
  headerTextColor?: string;
}

/* Obtención de logo en formato comprensible para Excel */
const getLogoBuffer = async (url: string): Promise<ArrayBuffer> => {
  const response = await fetch(url);
  return await response.arrayBuffer();
};

export const downloadXlsx = async (object: Excel, data: any) => {
  try {
    const workbook = new excel.Workbook();

    workbook.created = new Date();
    workbook.modified = new Date();
    workbook.lastPrinted = new Date();

    // Agregar hoja
    const sheet = workbook.addWorksheet('Hoja 1');

    // Logo Oldelval
    const logoBuffer = await getLogoBuffer('/assets/images/oldelval-logo.png');
    const logoId = workbook.addImage({ buffer: logoBuffer, extension: 'png' });

    for (let i = 0; i < 5; i++) {
      sheet.addRow([]);
    }

    sheet.addImage(logoId, {
      tl: { col: 0, row: 0 },
      ext: { width: 160, height: 90 }
    });

    sheet.addRow(object.headers);

    data.forEach((value: any) => {
      const items = object.keys.filter((k) =>
        Object.keys(value).includes(k.key as string)
      );
      const mapData = items.map((i) =>
        i.value ? i.value(value[i.key]) : value[i.key]
      );
      sheet.addRow(mapData);
    });

    sheet.columns = object.columnWidths;
    const rowNumber = 6;

    // Aplicar color de fondo a la primera fila
    const firstRow = sheet.getRow(rowNumber);
    firstRow.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: object.firstRowColor ?? 'FF0B6E93' },
      };
      cell.font = {
        color: { argb: object.color ?? 'FFFFFFFF' },
        bold: true,
      };
      cell.alignment = {
        horizontal: 'center',
        vertical: 'justify',
        wrapText: true,
      };
    });

    // Aplicar estilos a todas las filas y celdas
    sheet.eachRow((row) => {
      row.eachCell((cell, rowNumber) => {
        if (rowNumber <= 6) return;

        cell.alignment = {
          horizontal: 'justify',
          vertical: 'middle', // Cambiado a 'middle' para mejor alineación vertical
          wrapText: true, // Ajuste de texto automático
        };
      });
    });

    // Generar el archivo Excel como un buffer
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    // Crear un enlace de descarga y simular un clic
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = object.filename;
    link.click();

    // Liberar el objeto URL
    URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error(error);
  }
};

export const downloadMultiSheetXlsx = async (payload: MultiSheetPayload) => {
  try {
    const workbook = new excel.Workbook();
    
    workbook.created = new Date();
    workbook.modified = new Date();

    const headerFill = payload.headerColor ?? 'FF0B6E93';
    const headerText = payload.headerTextColor ?? 'FFFFFFFF';

    // Logo Oldelval
    const logoBuffer = await getLogoBuffer('/assets/images/oldelval-logo.png');
    const logoId = workbook.addImage({ buffer: logoBuffer, extension: 'png' });

    payload.sheets.forEach(sheetDef => {
      // Agregar hoja
      const sheet = workbook.addWorksheet(sheetDef.name || 'Hoja');
      const headers = sheetDef.headers ?? [];
      const headerRowNumber = 6;

      if (headers.length) {
        sheet.columns = headers.map((_, index) => ({
          // header, 
          key: `col${index}`,
          style: { alignment: { horizontal: 'center', vertical: 'middle', wrapText: true } },
        }));
      }

      // Insertar logo en cada hoja
      sheet.addImage(logoId, {
        tl: { col: 0, row: 0 },
        ext: { width: 150, height: 100 }
      });

      const headerRow = sheet.getRow(headerRowNumber);
      headerRow.values = headers; 
      
      headerRow.eachCell(cell => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: headerFill } };
        cell.font = { color: { argb: headerText }, bold: true };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      });

      // Insertar datos
      (sheetDef.rows || []).forEach(row => {
        const rowData: Record<string, any> = {};
        headers.forEach((_, idx) => {
          rowData[`col${idx}`] = row?.[idx] ?? '';
        });
        sheet.addRow(rowData);
      });
      
      sheet.columns?.forEach((column, index) => {
        const col = sheet.getColumn(index + 1);
        const colValues = col?.values ?? [];
        const headerLength = headers[index]?.length ?? 0;
        const maxLength = colValues.reduce<number>((acc, value) => {
          if (value === undefined || value === null) return acc;
          let asString: string;
          if (typeof value === 'object') {
            const anyVal = value as any;
            if (typeof anyVal.text === 'string') {
              asString = anyVal.text;
            } else if (Array.isArray(anyVal.richText)) {
              asString = anyVal.richText.map((rt: any) => rt?.text ?? '').join('');
            } else if (typeof anyVal.hyperlink === 'string') {
              asString = anyVal.hyperlink;
            } else {
              try {
                asString = JSON.stringify(value);
              } catch {
                asString = String(value);
              }
            }
          } else {
            asString = String(value);
          }
          return Math.max(acc, asString.length);
        }, headerLength);
        const adjustedWidth = Math.min(Math.max(maxLength + 2, 14), 60);
        column.width = Number.isFinite(adjustedWidth) ? adjustedWidth : 20;
      });
      const firstRowValues = sheet.rowCount > 1 ? sheet.getRow(2).values : null;
      const firstRowPreview = Array.isArray(firstRowValues)
        ? firstRowValues.map((value, index) => ({ index, value }))
        : firstRowValues;
      console.info('[downloadMultiSheetXlsx] worksheet stats', {
        name: sheet.name,
        headerCount: sheetDef.headers?.length ?? 0,
        rowCount: sheet.rowCount,
        columnCount: sheet.columnCount,
        firstDataRow: firstRowPreview,
      });
      if (Array.isArray(firstRowPreview)) {
        console.info('[downloadMultiSheetXlsx] first data row preview', JSON.stringify(firstRowPreview));
      }

      if (sheetDef.headers?.length) {
        const headerRow = sheet.getRow(headerRowNumber);
        headerRow.eachCell(cell => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: headerFill },
          };
          cell.font = {
            color: { argb: headerText },
            bold: true,
          };
          cell.alignment = {
            horizontal: 'center',
            vertical: 'middle',
            wrapText: true,
          };
        });
      }

      sheet.eachRow((row, rowNumber) => {
        if (rowNumber <= headerRowNumber) return;
        row.eachCell(cell => {
          cell.alignment = {
            horizontal: 'justify',
            vertical: 'middle',
            wrapText: true,
          };
        });
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    console.info('[downloadMultiSheetXlsx] buffer size', buffer.byteLength);
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    console.info('[downloadMultiSheetXlsx] blob size', blob.size);
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = payload.filename;
    link.click();
    URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error(error);
    throw error;
  }
};
